import CONFIG from "../../config/config";

const proxyURL = '/.netlify/functions/api-proxy';

// Helper function to make proxy requests
const makeProxyRequest = async (endpoint: string, method: string = 'GET', data?: any, userToken: string = '', authUserId?: string) => {
  const requestBody = {
    endpoint,
    method,
    data,
    userToken,
    authUserId, // Include authUserId for memeverse functions
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  };

  return fetch(proxyURL, requestOptions);
};

///////////////////////////// MemeVerse Feed ///////////////////////////////////
export const getHomeFeed = async (
  authUserId: string,
  numPosts: number,
  numComments: number,
  pageNumber: number,
  orderBy?: "recent" | "trending",
  userToken: string = ''
) => {
  orderBy ||= "recent";
  let queryParams = [
    `number_of_posts=${numPosts}`,
    `number_of_comments=${numComments}`,
    `page_no=${pageNumber}`,
    `order_by=${orderBy}`
  ].join('&')
  const endpoint = `api/memeverse/home_feed?` + queryParams;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getNewsFeed = async (
  authUserId: string,
  limit: number,
  numComments: number,
  pageNumber: number,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/meme-posts?user_id=${CONFIG.SOOPRA_NEWS_ID || 'LvOfCYkJxYlLfT8hQmlK'}&limit=${limit}&page=${pageNumber}&comment_limit=${numComments}&direction=descending`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getFollowingFeed = async (
  authUserId: string,
  limit: number,
  pageNo: number,
  userToken: string = ''
) => {
  let queryParams = [
    `user_id=${authUserId}`,
    `limit=${limit}`,
    `page=${pageNo}`
  ].join('&')
  const endpoint = `api/memeverse/meme-posts/get_post_by_following?` + queryParams;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

/////////////////////////////// Meme Posts /////////////////////////////////////
export const createPost = async (
  authUserId: string,
  content: string,
  images?: (File | string)[],
  userToken: string = ''
) => {
  const proxyURL = '/.netlify/functions/formdata-proxy';

  const formData = new FormData();

  // Add proxy configuration
  formData.append('endpoint', 'api/memeverse/meme-posts');
  formData.append('userToken', userToken);
  formData.append('authUserId', authUserId);

  // Add actual form data
  formData.append('user_id', authUserId);
  formData.append('content', content);

  const fileImages: File[] = [];
  const urlImages: string[] = [];

  images?.forEach((image) => {
    if (image instanceof File) {
      fileImages.push(image);
    } else if (typeof image === 'string') {
      urlImages.push(image);
    }
  });

  fileImages.forEach((file) => formData.append('images', file));

  if (urlImages.length) {
    formData.append('images', urlImages.join(','));
  }

  const requestOptions: RequestInit = {
    method: 'POST',
    body: formData,
  };

  return fetch(proxyURL, requestOptions);
};

export const getPostDetails = async (
  authUserId: string,
  postId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/meme-posts?post_id=${postId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getPostsByUser = async (
  authUserId: string,
  userId: string,
  limit: number,
  page: number,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/meme-posts?user_id=${userId}&limit=${limit}&page=${page}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const updatePost = async (
  authUserId: string,
  postId: string,
  content: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/meme-posts?post_id=${postId}`;
  const data = { content: content };

  return makeProxyRequest(endpoint, 'PUT', data, userToken, authUserId);
}

export const deletePost = async (
  authUserId: string,
  postId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/meme-posts?post_id=${postId}`;
  return makeProxyRequest(endpoint, 'DELETE', undefined, userToken, authUserId);
}

export const getUsersMyPosts = async (
  authUserId: string,
  userId: string,
  limit: number,
  page: number,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/meme-posts?user_id=${userId}&limit=${limit}&page=${page}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getUsersMyReplies = async (
  authUserId: string,
  userId: string,
  limit: number,
  page: number,
  userToken: string = ''
) => {
  const endpoint = `/api/memeverse/get_user_replied_post?user_id=${userId}&limit=${limit}&page=${page}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const generateMemes = async (
  userId: string,
  contentBody: string,
  userToken: string = ''
) => {
  const data = {
    user_id: userId,
    content_body: contentBody,
  };

  return makeProxyRequest('api/memeverse/generate_meme', 'POST', data, userToken);
}

export const incrementViewOnPost = async (
  authUserId: string,
  postId: string,
  userToken: string = ''
) => {
  const data = { post_id: postId };
  return makeProxyRequest('api/memeverse/meme-posts/increment_watch', 'POST', data, userToken, authUserId);
}

///////////////////////////// Meme Comments ////////////////////////////////////
export const createCommentOnPost = async (
  authUserId: string,
  postId: string,
  content: string,
  mentions?: string[],
  userToken: string = ''
) => {
  const data = {
    user_id: authUserId,
    commenter_type: "user",
    content: content,
    post_id: postId,
  };

  if (mentions && mentions.length) {
    data["mentioned_users"] = mentions;
  }

  return makeProxyRequest('api/memeverse/comment', 'POST', data, userToken, authUserId);
}

export const createCommentOnComment = async (
  commentId: string,
  postId: string,
  userId: string,
  content: string,
  mentions?: string[],
  userToken: string = ''
) => {
  const data = {
    user_id: userId,
    commenter_type: "user",
    content: content,
    comment_id: commentId,
    post_id: postId,
  };

  if (mentions && mentions.length) {
    data["mentioned_users"] = mentions;
  }

  return makeProxyRequest('api/memeverse/comment', 'POST', data, userToken, userId);
}

export const getCommentDetails = async (
  authUserId: string,
  commentId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/comment?comment_id=${commentId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getCommentsOnPost = async (
  authUserId: string,
  postId: string,
  limit: number,
  page: number,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/comment?post_id=${postId}&page=${page}&limit=${limit}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getCommentsByUser = async (
  authUserId: string,
  userId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/comment?user_id=${userId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const updateComment = async (
  authUserId: string,
  commentId: string,
  content: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/comment?comment_id=${commentId}`;
  const data = {
    comment: content,
    commenter_type: 'user',
  };

  return makeProxyRequest(endpoint, 'PUT', data, userToken, authUserId);
}

export const deleteComment = async (
  authUserId: string,
  commentId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/comment?comment_id=${commentId}`;
  return makeProxyRequest(endpoint, 'DELETE', undefined, userToken, authUserId);
}

export const incrementViewOnComment = async (
  authUserId: string,
  commentId: string,
  userToken: string = ''
) => {
  // Original function returned null, keeping that behavior
  return null;
}

//////////////////////////////// Post Likes ////////////////////////////////////
export const createPostlike = async (
  authUserId: string,
  postId: string,
  userToken: string = ''
) => {
  const data = {
    user_id: authUserId,
    post_id: postId,
  };

  return makeProxyRequest('api/memeverse/post_likes', 'POST', data, userToken, authUserId);
}

export const getPostLikes = async (
  authUserId: string,
  postId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/post_likes?post_id=${postId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getPostLikesByUser = async (
  authUserId: string,
  userId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/post_likes?user_id=${userId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getPostLikesByUserOnPost = async (
  authUserId: string,
  postId: string,
  userId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/post_likes?user_id=${userId}&post_id=${postId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

//////////////////////////////// Comment Likes ////////////////////////////////////
export const createCommentLike = async (
  authUserId: string,
  commentId: string,
  userToken: string = ''
) => {
  const data = {
    user_id: authUserId,
    comment_id: commentId,
  };

  return makeProxyRequest('api/memeverse/comment_likes', 'POST', data, userToken, authUserId);
}

export const getCommentLikesByUser = async (
  authUserId: string,
  userId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/comment_likes?user_id=${userId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

export const getCommentLikesByUserOnComment = async (
  authUserId: string,
  commentId: string,
  userId: string,
  userToken: string = ''
) => {
  const endpoint = `api/memeverse/comment_likes?user_id=${userId}&comment_id=${commentId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}

/////////////////////////////////// Follow /////////////////////////////////////
export const followCreator = async (
  authUserId: string,
  creatorId: string,
  userToken: string = ''
) => {
  const data = {
    user_id: authUserId,
    follow_user_id: creatorId,
  };

  return makeProxyRequest('api/memeverse/user_follow', 'POST', data, userToken, authUserId);
}

///////////////////////////// MemeVerse User Search ///////////////////////////////////
export const getMemeverseUsers = async (
  authUserId: string,
  query: string,
  pageNumber: number,
  pageSize: number,
  userToken: string = ''
) => {
  let queryParams = [
    `query=${query}`,
    `page=${pageNumber}`,
    `pageSize=${pageSize}`
  ].join('&')
  const endpoint = `search/ask_users?` + queryParams;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken, authUserId);
}
